import { SetStateAction, Dispatch, useEffect, useState } from "react";
import styled from "styled-components";
import { KeyValueObj } from "@/common/types";
import Input from "@/common/components/Input/Input";
import { states } from "@/common/data";
import { AddressManager } from "@/common/components/addresses";
import { MultiSelect } from "@/common/components/multi-selectInput";

export interface IReqFormProps {
  product: KeyValueObj;
  orders: KeyValueObj;
  setOrders: Dispatch<SetStateAction<KeyValueObj>>;
  type?: string;
}

interface ReqProps {
  isPreview: boolean;
}

const ReqFormWrapper = styled.div<ReqProps>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-around;
  background-color: ${({ theme }) => theme.bg.prim_500};
  padding: 2rem 0;

  .forms-wrapper {
    width: 90%;
    gap: 2rem;
    padding: 1rem 0;

    @media (max-width: ${({ theme }) => theme.screen.mobile}) {
      width: 100%;
      padding: 0;
    }

    .info,
    .inputs {
      width: 100%;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 1.5rem 1.1rem;
      padding: 0.85rem;

      .info-wrapper,
      .input-wrapper {
        width: 23%;
        align-items: flex-start;
        gap: 0.5rem;

        @media (max-width: ${({ theme }) => theme.screen.mobile}) {
          width: 46%;
        }

        h3 {
          font-weight: 500;
          font-size: 0.85rem;
          color: ${({ theme }) => theme.text.primary};
        }

        .title {
          color: ${({ theme }) => theme.text.prim_100};
          font-weight: 400;
        }
      }
    }

    .line {
      width: 90%;
      height: 1px;
      background-color: ${({ theme }) => theme.border.light};
    }

    .inputs {
      justify-content: flex-start;

      .input-wrapper {
        width: ${({ isPreview }) => (isPreview ? "22%" : "47%")};
        gap: 0.5rem;
        position: relative;

        @media (max-width: ${({ theme }) => theme.screen.mobile}) {
          width: 46%;
        }

        .input-error-message {
          color: red;
          font-size: 0.8rem;
        }

        input,
        textarea,
        select {
          width: 100%;
          padding: 0.65rem;
          outline: none;
          border: 1px solid ${({ theme }) => theme.border.input};
          border-radius: 0.25rem;
          resize: none;
          color: ${({ theme }) => theme.text_prim_100};
          font-weight: 400;
          font-size: 0.8rem;
        }
      }

      .buyer-note {
        flex-basis: 100%;
        align-self: stretch;
      }
    }
  }
`;

export function ReqForm({ product, orders, setOrders, type }: IReqFormProps) {
  //STATES
  const [formValues, setFormValues] = useState({});
  const [selectedProducts, setSelectedProducts] = useState<
    {
      id: string;
      title: string;
      amount: number;
      quantity: number;
      sellingPrice: string;
    }[]
  >([]);
  const isPreview = type === "preview";
  const id = product?.id;

  console.log("Batches... ", product?.batches);

  //USEEFFECTS
  useEffect(() => {
    if (isPreview) return;
    setOrders((prev: KeyValueObj) => ({
      ...prev,
      [product.id]: {
        ...prev[product.id],
        selected_batches: selectedProducts,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  //initialize the values
  useEffect(() => {
    if (isPreview) return;
    setOrders((prev: any) => {
      return {
        ...prev,
        [product?.id]: {
          ...prev[product?.id],
          product_id: product?.id,
          supplier_id: prev[product?.id]?.supplier_id ?? "",
          package_type: prev[product?.id]?.package_type ?? "",
        },
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const batchOptions = (product.batches ?? []).map((batch: any) => ({
    id: batch.inventory_id,
    title: batch.batch_number,
    quantity: batch.quantity_in_stock,
    sellingPrice: batch.selling_price_per_unit,
  }));

  return (
    <ReqFormWrapper isPreview={isPreview}>
      <div className="forms-wrapper flex-col">
        <div className="info flex-row">
          <div className="info-wrapper flex-col">
            <h3 className="title">Name</h3>
            <h3 className="value">{product?.atc_code?.code_title}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">ATC Code</h3>
            <h3 className="value">{product?.atc_code?.code_identifier}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Medex Code</h3>
            <h3 className="value">{product?.medex_code}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Brand</h3>
            <h3 className="value">{product?.brand_name}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Product strength</h3>
            <h3 className="value">
              {product?.strength + product?.strength_unit}
            </h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Form</h3>
            <h3 className="value">{product?.form}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Classification</h3>
            <h3 className="value">{product?.classification}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">Regulation</h3>
            <h3 className="value">{product?.regulation}</h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">
              Number of{" "}
              {`${product.secondary_packaging} per ${product.tertiary_packaging}`}{" "}
            </h3>
            <h3 className="value">
              {product?.secondary_package_quantity_per_tertiary_package}
            </h3>
          </div>

          <div className="info-wrapper flex-col">
            <h3 className="title">
              Number of{" "}
              {`${product.primary_packaging} per ${product.secondary_packaging}`}
            </h3>
            <h3 className="value">
              {product?.primary_package_quantity_per_secondary_package}
            </h3>
          </div>
        </div>

        <div className="line"></div>
        <div className="inputs flex-row">
          {!isPreview ? (
            <>
              <MultiSelect
                fullWidth
                name="products"
                prompt="Select Products batches"
                placeholder="Choose batches"
                options={batchOptions}
                returnId
                formValues={formValues}
                setFormValues={setFormValues}
                onEveryChange={(selectedIds) => {
                  setSelectedProducts((prev) => {
                    const existingAmounts = Object.fromEntries(
                      prev.map((item) => [item.id, item.amount]),
                    );

                    return batchOptions
                      .filter((batch: any) => selectedIds.includes(batch.id))
                      .map((batch: any) => ({
                        id: batch.id,
                        title: batch.title,
                        amount: existingAmounts[batch.id] || 0,
                        quantity: batch.quantity,
                        sellingPrice: batch.sellingPrice,
                      }));
                  });
                }}
              />

              <div className="mt-4 space-y-3 w-full">
                {selectedProducts.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 border rounded p-3"
                  >
                    <div className="flex flex-col flex-1 gap-1">
                      <span className="font-medium text-sm">{item.title}</span>
                      <span className="text-xs text-gray-500">
                        In stock: {item.quantity} units · Price: ₦
                        {Number(item.sellingPrice).toLocaleString()} / unit
                      </span>
                    </div>
                    <input
                      type="number"
                      min={0}
                      placeholder="Qty"
                      onChange={(e) => {
                        const amount = Number(e.target.value);
                        setSelectedProducts((prev) =>
                          prev.map((p) =>
                            p.id === item.id ? { ...p, amount } : p,
                          ),
                        );
                      }}
                      className="border rounded px-3 py-2 w-24"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="input-wrapper flex-col" style={{ width: "100%" }}>
              <h3 className="title">Selected Batches</h3>
              {(orders[id]?.selected_batches ?? []).length === 0 ? (
                <h3 className="value">No batches selected</h3>
              ) : (
                <div className="mt-2 space-y-2 w-full">
                  {(
                    orders[id]?.selected_batches as {
                      id: string;
                      title: string;
                      amount: number;
                      quantity: number;
                      sellingPrice: string;
                    }[]
                  ).map((batch) => (
                    <div
                      key={batch.id}
                      className="flex justify-between items-center w-full border rounded p-2"
                    >
                      <span className="font-medium text-sm">{batch.title}</span>
                      <span className="text-xs text-gray-500">
                        Qty ordered: {batch.amount} · ₦
                        {Number(batch.sellingPrice).toLocaleString()} / unit
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ReqFormWrapper>
  );
}

export interface IOrderDeliveryDetailsProps {
  isPreview?: boolean;
  deliveryDetails: KeyValueObj;
  setDeliveryDetails: Dispatch<SetStateAction<KeyValueObj>>;
}

export function OrderDeliveryDetails({
  isPreview,
  deliveryDetails,
  setDeliveryDetails,
}: IOrderDeliveryDetailsProps) {
  const [selectedAddress, setSelectedAddress] = useState<KeyValueObj>({});

  useEffect(() => {
    setDeliveryDetails((prev: any) => ({ ...prev, ...selectedAddress }));

    // eslint-disable-next-line
  }, [selectedAddress]);

  function updateData(name: string, value: any) {
    setDeliveryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  return (
    <ReqFormWrapper isPreview={!!isPreview}>
      <div className="forms-wrapper flex-col">
        <div className="inputs flex-row">
          {!isPreview ? (
            <>
              <AddressManager
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
              />
              <>
                <Input
                  placeholder={"Enter order note"}
                  prompt={"Order Note"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"buyer_note"}
                  inputType="textarea"
                  indexLevel={5}
                  updateData={updateData}
                  options={states}
                  updateValidity={(_name: string, _validity: boolean) => {}}
                  formValues={deliveryDetails}
                  setFormValues={setDeliveryDetails}
                />

                <Input
                  placeholder={"Enter delivery date"}
                  prompt={"Expected Delivery Date"}
                  fullWidth={false}
                  shortWidth={false}
                  name={"expected_delivery_date"}
                  inputType="date"
                  indexLevel={6}
                  updateData={updateData}
                  options={states}
                  updateValidity={(_name: string, _validity: boolean) => {}}
                  formValues={deliveryDetails}
                  setFormValues={setDeliveryDetails}
                  rules={{
                    notInTheFuture: {
                      expectedValue: false,
                      errorMessage:
                        "Expected delivery date cannot be in the past",
                    },
                  }}
                />
              </>
            </>
          ) : null}

          {isPreview ? (
            <>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Delivery State</h3>
                <h3 className="preview">{deliveryDetails?.state}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Delivery Lga</h3>
                <h3 className="preview">{deliveryDetails?.lga}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Delivery City</h3>
                <h3 className="preview">{deliveryDetails?.city}</h3>
              </div>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Address Line one</h3>
                <h3 className="preview">{deliveryDetails?.address_line_one}</h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Address Line two</h3>
                <h3 className="preview">{deliveryDetails?.address_line_two}</h3>
              </div>
              <div className="input-wrapper  flex-col">
                <h3 className="title">Postal Code</h3>
                <h3 className="preview">{deliveryDetails?.postal_code}</h3>
              </div>

              <div className="input-wrapper flex-col">
                <h3 className="title"> Expected Delivery Date</h3>
                <h3 className="preview">
                  {deliveryDetails?.expected_delivery_date}
                </h3>
              </div>
              <div className="input-wrapper flex-col">
                <h3 className="title">Buyer Note</h3>
                <h3 className="preview">{deliveryDetails?.buyer_note}</h3>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </ReqFormWrapper>
  );
}
